/**
 * useBusinessContext — the AI-sharpened business-context brief on the project Overview (T-35).
 *
 * Served by the separate Provisioning API (see `useProvisioningClient` — infra/stacks/
 * provisioning-infra.ts), same Cognito auth:
 *   GET  /api/projects/{id}/business-context            → brief | 404 NOT_GENERATED (→ `null` here)
 *   POST /api/projects/{id}/business-context/generate   → brief (409 BRIEF_EDITED without overwrite)
 *   PUT  /api/projects/{id}/business-context            → brief
 *
 * Every request opts out of the global error toast: the card renders its own error / empty states,
 * and a "not generated yet" 404 is the normal empty state, not an error.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiErrorCode, useProvisioningClient } from "@/lib/api";
import type { BusinessContext, BusinessContextGenerateResponse } from "@/types";

export const businessContextQueryKey = (projectId: string): readonly unknown[] => [
  "business-context",
  projectId,
];

/**
 * Generation is one synchronous Bedrock call behind API Gateway's 29 s cap — well past the shared
 * axios instance's 10 s default.
 */
export const BUSINESS_CONTEXT_GENERATE_TIMEOUT_MS = 30_000;

export function useBusinessContext(projectId: string) {
  const client = useProvisioningClient();
  return useQuery({
    queryKey: businessContextQueryKey(projectId),
    queryFn: async (): Promise<BusinessContext | null> => {
      try {
        const res = await client.get<BusinessContext>(
          `/api/projects/${encodeURIComponent(projectId)}/business-context`,
          { _suppressErrorToast: true },
        );
        return res.data;
      } catch (err) {
        if (apiErrorCode(err) === "NOT_GENERATED") return null;
        throw err;
      }
    },
    enabled: Boolean(projectId),
    retry: false,
  });
}

export interface GenerateBusinessContextInput {
  /** Replace a hand-edited brief (the server answers 409 BRIEF_EDITED otherwise). */
  overwriteEdits?: boolean;
}

export function useGenerateBusinessContext(projectId: string) {
  const client = useProvisioningClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ overwriteEdits }: GenerateBusinessContextInput = {}) => {
      const res = await client.post<BusinessContextGenerateResponse>(
        `/api/projects/${encodeURIComponent(projectId)}/business-context/generate`,
        overwriteEdits ? { overwrite_edits: true } : {},
        { _suppressErrorToast: true, timeout: BUSINESS_CONTEXT_GENERATE_TIMEOUT_MS },
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(businessContextQueryKey(projectId), data);
    },
  });
}

export function useSaveBusinessContext(projectId: string) {
  const client = useProvisioningClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (contentMd: string) => {
      const res = await client.put<BusinessContext>(
        `/api/projects/${encodeURIComponent(projectId)}/business-context`,
        { content_md: contentMd },
        { _suppressErrorToast: true },
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(businessContextQueryKey(projectId), data);
    },
  });
}
